import {create} from 'zustand';
import {subscribeWithSelector} from 'zustand/middleware';
import {authorizedFetch} from '@nimbitlabs/ui';
import type {ProductDto} from './types';
import {useAccountStore} from '../account/accountStore';

export interface SubscriptionLimits {
    plan_name: string;
    is_unlimited?: boolean;
    features?: {
        [key: string]: {
            limit: number;
            requires_paid: boolean;
        };
    };
}
export interface AccountUsage {
    users: number;
    services: number;
    teams: number;
    environments: number;
    apiKeys: number;
    monitoringChecks: number;
}
export interface CreateSubscriptionDto {
    planCode: string;
    amount: number;
    billingInterval: string;
    callbackUrl: string;
}
export interface CreateSubscriptionResponse {
    subscriptionId: string;
    authorizationUrl: string | null;
    requiresPayment: boolean;
    isDowngrade: boolean;
    scheduledStartDate: string | null;
    message: string;
}
interface SubscriptionState {
    limits: SubscriptionLimits | null;
    usage: AccountUsage | null;
    products: ProductDto[];
    activeSubscription: any;
    scheduledSubscriptions: any[];
    loading: boolean;
    error: string | null;

    fetchLimits: () => Promise<void>;
    fetchUsage: () => Promise<void>;
    fetchProducts: () => Promise<void>;
    fetchActiveSubscription: () => Promise<void>;
    fetchScheduledSubscriptions: () => Promise<void>;
    createSubscription: (data: CreateSubscriptionDto) => Promise<CreateSubscriptionResponse>;
    invalidateAll: () => Promise<void>;
    clearError: () => void;
    reset: () => void;
}
export const useSubscriptionStore = create<SubscriptionState>()(
    subscribeWithSelector((set, get) => ({
        limits: null,
        usage: null,
        products: [],
        activeSubscription: null,
        scheduledSubscriptions: [],
        loading: false,
        error: null,

        fetchLimits: async () => {
            set({loading: true, error: null});
            try {
                const response = await authorizedFetch('/subscriptions/limits');
                const limits = await response.json() as SubscriptionLimits;
                set({limits, loading: false});
            } catch (e: any) {
                set({error: e?.message ?? 'Failed to fetch subscription limits', loading: false});
            }
        },

        fetchUsage: async () => {
            set({loading: true, error: null});
            try {
                const response = await authorizedFetch('/subscriptions/usage');
                const usage = await response.json() as AccountUsage;
                set({usage, loading: false});
            } catch (e: any) {
                set({error: e?.message ?? 'Failed to fetch account usage', loading: false});
            }
        },

        fetchProducts: async () => {
            set({loading: true, error: null});
            try {
                const response = await authorizedFetch('/products');
                const products = await response.json() as ProductDto[];
                set({products, loading: false});
            } catch (e: any) {
                set({error: e?.message ?? 'Failed to fetch products', loading: false});
            }
        },

        fetchActiveSubscription: async () => {
            set({loading: true, error: null});
            try {
                const response = await authorizedFetch('/subscriptions/active');
                const activeSubscription = await response.json();
                set({activeSubscription, loading: false});
            } catch (e: any) {
                set({error: e?.message ?? 'Failed to fetch active subscription', loading: false});
            }
        },

        fetchScheduledSubscriptions: async () => {
            set({loading: true, error: null});
            try {
                const response = await authorizedFetch('/subscriptions/scheduled');
                const scheduledSubscriptions = await response.json();
                set({scheduledSubscriptions, loading: false});
            } catch (e: any) {
                set({error: e?.message ?? 'Failed to fetch scheduled subscriptions', loading: false});
            }
        },

        createSubscription: async (data: CreateSubscriptionDto) => {
            try {
                const response = await authorizedFetch('/subscriptions', {
                    method: 'POST',
                    body: JSON.stringify(data),
                });
                const result = await response.json() as CreateSubscriptionResponse;
                return result;
            } catch (e: any) {
                set({error: e?.message ?? 'Failed to create subscription'});
                throw e;
            }
        },

        invalidateAll: async () => {
            await Promise.all([
                get().fetchLimits(),
                get().fetchUsage(),
                get().fetchActiveSubscription(),
                get().fetchScheduledSubscriptions(),
            ]);
        },

        clearError: () => set({error: null}),

        reset: () => set({
            limits: null,
            usage: null,
            products: [],
            activeSubscription: null,
            scheduledSubscriptions: [],
            loading: false,
            error: null
        }),
    })));

// Subscribe to account changes and auto-fetch subscription data
useAccountStore.subscribe(
    (state) => state.currentAccount,
    (currentAccount) => {
        if (currentAccount?.id) {
            useSubscriptionStore.getState().fetchLimits();
            useSubscriptionStore.getState().fetchUsage();
            useSubscriptionStore.getState().fetchActiveSubscription();
            useSubscriptionStore.getState().fetchScheduledSubscriptions();
        } else {
            useSubscriptionStore.getState().reset();
        }
    }
);

// Subscribe to active subscription changes (e.g., tier changes)
// When subscription changes, refetch limits and usage to reflect new tier
useSubscriptionStore.subscribe(
    (state) => state.activeSubscription,
    (activeSubscription, previousActiveSubscription) => {
        // Only refetch if subscription actually changed (not initial load)
        if (activeSubscription && previousActiveSubscription &&
            activeSubscription.id !== previousActiveSubscription.id) {
            console.log('[SubscriptionStore] Subscription changed, refetching limits and usage');
            useSubscriptionStore.getState().fetchLimits();
            useSubscriptionStore.getState().fetchUsage();
        }
    }
);

// Helper to check if user is on free tier
export function isFreeTier(limits: SubscriptionLimits | null): boolean {
    return !limits
        || !limits.plan_name
        || limits.plan_name.toLowerCase() === 'free';
}

// Hook to check if user is on free tier
export function useIsFreeTier(): boolean {
    const limits = useSubscriptionStore((state) => state.limits);
    return isFreeTier(limits);
}

// Helper to check if user is on paid tier
export function isPaidTier(limits: SubscriptionLimits | null): boolean {
    return !isFreeTier(limits);
}

// Helper to get plan name
export function getPlanName(limits: SubscriptionLimits | null): string {
    return limits?.plan_name || 'free';
}
